import { useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Appointment, AppointmentStatus } from '../types/appointment'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { formatPatientName, formatUserName } from '../utils/name'
import { mapStcReasonsToSpecialServiceCode } from '../utils/stcReasonToCode'
import { appointmentService } from '../services/appointmentService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AppointmentCancelModal } from './AppointmentCancelModal'
import { QuickStartConsultationForm } from './QuickStartConsultationForm'
import { StcSigning } from '../pages/StcSigning'
import { Modal } from './Modal'
import './AppointmentKanban.css'

interface AppointmentKanbanProps {
  appointments: Appointment[]
  patients: Patient[]
  staff: User[]
  onAppointmentClick?: (appointment: Appointment) => void
}

type StatusColumn = {
  id: Appointment['status']
  title: string
  color: string
}

const STATUS_COLUMNS: StatusColumn[] = [
  { id: 'scheduled', title: 'Scheduled', color: '#3b82f6' },
  { id: 'arrived', title: 'Arrived', color: '#22c55e' },
  { id: 'in-progress', title: 'In Progress', color: '#f59e0b' },
  { id: 'completed', title: 'Completed', color: '#6b7280' },
  { id: 'cancelled', title: 'Cancelled', color: '#ef4444' },
  { id: 'dna', title: 'DNA', color: '#dc3545' },
]

interface AppointmentCardProps {
  appointment: Appointment
  patient?: Patient
  staffMember?: User
  onClick?: () => void
  onCancel?: (appointment: Appointment) => void
  onStartConsultation?: (appointment: Appointment) => void
  onStcBadgeClick?: (appointment: Appointment) => void
}

function AppointmentCard({ appointment, patient, staffMember, onClick, onCancel, onStartConsultation, onStcBadgeClick }: AppointmentCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: appointment.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const patientName = patient ? formatPatientName(patient as any) : 'Unknown Patient'
  const staffName = staffMember ? formatUserName(staffMember as any) : 'Unknown Staff'

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onCancel) {
      onCancel(appointment)
    }
  }

  const handleStartConsultationClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onStartConsultation) {
      onStartConsultation(appointment)
    }
  }

  const handleStcBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onStcBadgeClick) {
      onStcBadgeClick(appointment)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="appointment-card-kanban"
      onClick={onClick}
    >
      <div className="appointment-card-header">
        <h4>{patientName}</h4>
        <span className="appointment-time">
          {appointment.startTime} - {appointment.endTime}
        </span>
      </div>
      <div className="appointment-card-body">
        <p className="appointment-staff">With: {staffName}</p>
        <p className="appointment-type">{appointment.type}</p>
        {appointment.reason && (
          <p className="appointment-reason">{appointment.reason}</p>
        )}
        {patient && patient.dnaCount && patient.dnaCount > 0 && appointment.status !== 'dna' && (
          <div className="dna-warning-badge">
            ⚠️ {patient.dnaCount === 1 
              ? 'DNA\'d last appointment' 
              : `DNA'd last ${patient.dnaCount} appointment${patient.dnaCount > 1 ? 's' : ''}`}
            {patient.dnaHistory && patient.dnaHistory.length > 0 && !patient.dnaHistory[0].dnaReason && (
              <span className="dna-no-reason"> (no reason)</span>
            )}
          </div>
        )}
      </div>
      <div className="appointment-card-footer">
        <span className="appointment-date">{appointment.appointmentDate}</span>
        <div className="appointment-card-footer-right">
          {appointment.status === 'completed' && appointment.stcSuggested && (
            appointment.stcRaised ? (
              <span
                className="stc-indicator-badge stc-indicator-badge-disabled"
                title={appointment.stcRaisedAt ? `STC signed ${new Date(appointment.stcRaisedAt).toLocaleString()}` : 'STC signed'}
                aria-label="STC signed"
              >
                STC
              </span>
            ) : (
              <button
                type="button"
                className="stc-indicator-badge stc-indicator-badge-button"
                title={appointment.stcSuggestedReasons?.length ? `STC signature needed: ${appointment.stcSuggestedReasons.join(', ')}` : 'STC signature needed'}
                aria-label="STC signature required – open form"
                onClick={handleStcBadgeClick}
              >
                STC
              </button>
            )
          )}
          <div className="appointment-card-actions">
          {appointment.status === 'arrived' && onStartConsultation && (
            <button
              className="appointment-card-start-consultation-btn"
              onClick={handleStartConsultationClick}
              title="Start consultation"
            >
              🏥
            </button>
          )}
          {appointment.status !== 'cancelled' && appointment.status !== 'completed' && appointment.status !== 'arrived' && appointment.status !== 'in-progress' && onCancel && (
            <button
              className="appointment-card-cancel-btn"
              onClick={handleCancelClick}
              title="Cancel appointment"
            >
              ✕
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function AppointmentKanban({
  appointments,
  patients,
  staff,
  onAppointmentClick,
}: AppointmentKanbanProps) {
  const queryClient = useQueryClient()
  const [activeId, setActiveId] = useState<string | null>(null)
  const [cancelModalAppointment, setCancelModalAppointment] = useState<Appointment | null>(null)
  const [consultationModalAppointment, setConsultationModalAppointment] = useState<Appointment | null>(null)
  const [stcModalAppointment, setStcModalAppointment] = useState<Appointment | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Appointment['status'] }) =>
      appointmentService.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.cancel(id, reason),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      // Update all appointment queries (with or without filters)
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      // Get all appointment queries and update them optimistically
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      // Optimistically update all appointment queries
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'cancelled' as AppointmentStatus, cancelledAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      // Refetch all appointment queries to get the latest data from server
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setCancelModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to cancel appointment:', error)
      alert('Failed to cancel appointment. Please try again.')
    },
  })

  const handleCancel = (appointment: Appointment) => {
    setCancelModalAppointment(appointment)
  }

  const handleStartConsultation = (appointment: Appointment) => {
    setConsultationModalAppointment(appointment)
  }

  const handleStcBadgeClick = (appointment: Appointment) => {
    setStcModalAppointment(appointment)
  }

  const handleCancelConfirm = async (reason?: string) => {
    if (cancelModalAppointment) {
      await cancelMutation.mutateAsync({ id: cancelModalAppointment.id, reason })
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const appointmentId = active.id as string
    const newStatus = over.id as Appointment['status']

    const appointment = appointments.find((a) => a.id === appointmentId)
    if (!appointment || appointment.status === newStatus) return

    updateStatusMutation.mutate({ id: appointmentId, status: newStatus })
  }

  // Group appointments by status
  const appointmentsByStatus = STATUS_COLUMNS.reduce((acc, column) => {
    acc[column.id] = appointments.filter((a) => a.status === column.id)
    return acc
  }, {} as Record<Appointment['status'], Appointment[]>)

  const activeAppointment = activeId
    ? appointments.find((a) => a.id === activeId)
    : null

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="appointment-kanban">
        {STATUS_COLUMNS.map((column) => {
          const columnAppointments = appointmentsByStatus[column.id] || []
          return (
            <div key={column.id} className="kanban-column">
              <div
                className="kanban-column-header"
                style={{ borderTopColor: column.color }}
              >
                <h3>{column.title}</h3>
                <span className="kanban-count">{columnAppointments.length}</span>
              </div>
              <SortableContext
                items={columnAppointments.map((a) => a.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="kanban-column-body">
                  {columnAppointments.map((appointment) => {
                    const patient = patients.find((p) => p.id === appointment.patientId)
                    const staffMember = staff.find((s) => s.id === appointment.staffId)
                    return (
                      <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        patient={patient}
                        staffMember={staffMember}
                        onClick={() => onAppointmentClick?.(appointment)}
                        onCancel={handleCancel}
                        onStartConsultation={handleStartConsultation}
                        onStcBadgeClick={handleStcBadgeClick}
                      />
                    )
                  })}
                </div>
              </SortableContext>
            </div>
          )
        })}
      </div>
      <DragOverlay>
        {activeAppointment ? (
          <AppointmentCard
            appointment={activeAppointment}
            patient={patients.find((p) => p.id === activeAppointment.patientId)}
            staffMember={staff.find((s) => s.id === activeAppointment.staffId)}
            onStcBadgeClick={handleStcBadgeClick}
          />
        ) : null}
      </DragOverlay>

      {cancelModalAppointment && (
        <AppointmentCancelModal
          appointment={{
            id: cancelModalAppointment.id,
            patientName: formatPatientName(patients.find((p) => p.id === cancelModalAppointment.patientId) as any) || 'Unknown Patient',
            staffName: formatUserName(staff.find((s) => s.id === cancelModalAppointment.staffId) as any) || 'Unknown Staff',
            appointmentDate: cancelModalAppointment.appointmentDate,
            startTime: cancelModalAppointment.startTime,
            type: cancelModalAppointment.type,
          }}
          isOpen={!!cancelModalAppointment}
          onClose={() => setCancelModalAppointment(null)}
          onConfirm={handleCancelConfirm}
          isLoading={cancelMutation.isPending}
        />
      )}

      {consultationModalAppointment && (
        <Modal
          isOpen={!!consultationModalAppointment}
          onClose={() => setConsultationModalAppointment(null)}
          title="Start Consultation"
          size="medium"
        >
          <QuickStartConsultationForm
            patientId={consultationModalAppointment.patientId}
            appointmentId={consultationModalAppointment.id}
            initialStaffId={consultationModalAppointment.staffId}
            initialReason={consultationModalAppointment.reason}
            initialReasonCode={consultationModalAppointment.reasonCode}
            initialPregnancyId={consultationModalAppointment.pregnancyId}
            onCancel={() => setConsultationModalAppointment(null)}
          />
        </Modal>
      )}

      {stcModalAppointment && stcModalAppointment.stcSuggestedConsultationId && (
        <Modal
          isOpen={!!stcModalAppointment}
          onClose={() => setStcModalAppointment(null)}
          title={`STC – ${formatPatientName(patients.find((p) => p.id === stcModalAppointment.patientId) as any) || 'Patient'}`}
          size="large"
        >
          <div className="stc-modal-body">
            <StcSigning
              embedded
              consultationIdProp={stcModalAppointment.stcSuggestedConsultationId}
              patientIdProp={stcModalAppointment.patientId}
              patientNameProp={formatPatientName(patients.find((p) => p.id === stcModalAppointment.patientId) as any) || undefined}
              initialLocationProp="S"
              initialSpecialServiceProp={mapStcReasonsToSpecialServiceCode(stcModalAppointment.stcSuggestedReasons)}
              appointmentIdProp={stcModalAppointment.id}
              onClose={() => setStcModalAppointment(null)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['stc-records'] })
                queryClient.invalidateQueries({ queryKey: ['appointments'] })
                setStcModalAppointment(null)
              }}
            />
            <p className="stc-modal-link">
              <Link to={`/patients/${stcModalAppointment.patientId}`} onClick={() => setStcModalAppointment(null)}>
                View patient record
              </Link>
            </p>
          </div>
        </Modal>
      )}
    </DndContext>
  )
}

